import React, { useEffect, useState } from "react";
import "./App.css";

import {
  WorldState,
  WorldHandler,
  WorldPos,
  ActionType,
  Experiments,
} from "./controllers/world";
import { CharacterHandler, StatusType } from "./controllers/characters";
import { ResourceHandler as RH, ResourceType } from "./controllers/resources";
import WorldComp from "./components/World";
import SquareDetails from "./components/SquareDetails";
import CharacterDetails from "./components/CharacterDetails";
import PlayOptions from "./components/PlayOptions";
import WorldCharts from "./components/WorldCharts";

/*
import { learnGuards } from "./controllers/animals";
learnGuards();
throw new Error("stop");
*/

import config from "./worldConfig";
import Families from "./components/Families";
import ActionSwitchOptions from "./components/ActionSwitchOptions";
import { Col, Row } from "antd";
import { getStats, StatisticState } from "./controllers/statistics";
import { GeneTrait } from "./controllers/genetics";
import { Faction, FactionType, SocialHandler } from "./controllers/social";
import { War } from "./controllers/war";

const { WORLD_SIZE, POP_FACTOR } = config;

export enum MapHighlightMode {
  POP = "POP",
  FOOD = "FOOD",
  ANIMAL_PREY = "ANIMAL_PREY",
}

export enum RenderMode {
  NONE,
  REACT_GRID,
}

type ViewState = {
  playMode: PlayMode;
  squareHighlighted: WorldPos | null;
  characterHighlighted: string | null;
  speed: number;
  mapHighlightMode: MapHighlightMode;
  renderModes: RenderMode[];
};

export enum PlayMode {
  PLAY = "PLAY",
  PAUSE = "PAUSE",
}

//const availableActions = orderedActionsAll;
const availableActions: ActionType[] = [
  ActionType.AGE,
  ActionType.HUNT,
  ActionType.EAT,
  ActionType.DIE,
  ActionType.SEX,
  ActionType.BIRTH,
  ActionType.MOVE,
  ActionType.WAR,
  ActionType.UPDATE_STATUS_LIST,
  ActionType.PREY_REPRODUCE,
];

const initialView: ViewState = {
  playMode: PlayMode.PLAY,
  squareHighlighted: null,
  characterHighlighted: null,
  speed: 1,
  mapHighlightMode: MapHighlightMode.ANIMAL_PREY,
  renderModes: [RenderMode.REACT_GRID],
};

const initialStatistics: StatisticState = {
  charactersTotal: [{ cycle: 0, value: 0 }],
  deadsTotal: [{ cycle: 0, value: 0 }],
  deadsPerCycle: [{ cycle: 0, value: 0 }],
  deathCausesPerCycle: [
    {
      cycle: 0,
      value: [
        StatusType.STARVING,
        StatusType.OLD,
        StatusType.WOUNDED,
        StatusType.PREGNANT,
      ].map((c) => ({
        type: c,
        value: 0,
      })),
    },
  ], //@TODO: make cause list adaptative
  ageMax: [{ cycle: 0, value: 0 }],
  geneTraits: [
    {
      cycle: 0,
      value: Object.values(GeneTrait).map((c) => ({
        type: c,
        value: 0,
      })),
    },
  ],
  status: [
    {
      cycle: 0,
      value: Object.values(StatusType).map((c) => ({
        type: c,
        value: 0,
      })),
    },
  ],
};

export type AppState = {
  view: ViewState;
  world: WorldState;
  statistics: StatisticState;
};

const initialWorld = (() => {
  const characterNum = WORLD_SIZE * WORLD_SIZE * POP_FACTOR;
  const familiesNum = characterNum;
  const familyFactions: Faction<FactionType.FAMILY>[] = Array.from(
    Array(familiesNum)
  ).map((e, i) =>
    SocialHandler.createFaction(
      FactionType.FAMILY,
      `${FactionType.FAMILY} ${i}`
    )
  );

  let w = WorldHandler.create(WORLD_SIZE, {
    actionsListOrdered: availableActions,
    experiments: [Experiments.LASTNAME_TO_GENES],
    factions: familyFactions,
  });

  console.log("world", w);

  const foodNodes = RH.getGenericRandomNodes(w, ResourceType.FOOD, 1, 0, 2);

  const animalPreyNodes = RH.getGenericRandomNodes(
    w,
    ResourceType.ANIMAL_PREY,
    0.95,
    100,
    100
  );

  const wars: War<FactionType.FAMILY>[] = [
    {
      factionGroupA: [
        familyFactions[0],
        familyFactions[1],
        familyFactions[2],
        familyFactions[3],
      ],
      factionGroupB: [
        familyFactions[4],
        familyFactions[5],
        familyFactions[6],
        familyFactions[7],
      ],
      score: 0,
    },
  ];

  w = {
    ...w,
    characters: [
      ...Array.from(Array(characterNum)).map((e, i) =>
        CharacterHandler.create(w, {
          age: 20 * 12, // Math.floor(Math.random() * CHARACTER_AGE_OLD_START),
          factions: SocialHandler.addFactionMembership(
            { factions: [] },
            familyFactions[i]
          ).factions,
        })
      ),
    ],
    wars,
    resources: [...foodNodes, ...animalPreyNodes],
  };

  return WorldHandler.updateFactionPos(w);
})();

type GlobalContext = {
  appState: AppState;
  setAppState: (a: AppState) => void;
};

const baseContext = {
  view: initialView,
  world: initialWorld,
  statistics: initialStatistics,
};

export const MainContext = React.createContext<GlobalContext>({
  appState: baseContext,
  setAppState: (a: AppState) => {},
});

function App() {
  const [appState, setAppState] = useState<AppState>(baseContext);

  const onTick = () => {
    const newWorld = WorldHandler.turn(appState.world);

    if (appState.view.playMode === PlayMode.PLAY) {
      setAppState({
        ...appState,
        world: newWorld,
        statistics: getStats(newWorld, appState.statistics),
      });
    }
    //console.log("VIEW", appState.view);
  };

  useEffect(() => {
    const timer = setTimeout(onTick, 2000 / appState.view.speed);

    return () => {
      clearTimeout(timer);
    };
  });

  return (
    <Row>
      <MainContext.Provider
        value={{
          appState: appState,
          setAppState: (a: AppState) => setAppState(a),
        }}
      >
        <Col span={8}>
          <PlayOptions></PlayOptions>
          <ActionSwitchOptions />
          <SquareDetails></SquareDetails>
        </Col>
        <Col span={8}>
          {appState.world != null ? <WorldComp /> : "NO WORLD FOUND"}
          <WorldCharts />
          <Families />
        </Col>
        <Col span={8}>
          <CharacterDetails></CharacterDetails>
        </Col>
      </MainContext.Provider>
    </Row>
  );
}

export default App;
